<script lang="ts">
  import { mdiArrowRightCircleOutline } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import type { OrgPresentationMode } from "../../../../../../_shared/_logic/presentation-mode"
  import { normalizePortalCode } from "../../_shared/_logic/appointment-portal"
  import { buildCodeGateDescription } from "./_logic/codeGatePresentation"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"

  let {
    loading = false,
    error = null,
    presentationMode = "appointment_portal_only",
    hasCodeInUrl = false,
    onsubmit,
  }: {
    loading?: boolean
    error?: string | null
    presentationMode?: OrgPresentationMode
    hasCodeInUrl?: boolean
    onsubmit: (code: string) => void
  } = $props()

  let code = $state("")
  let localError = $state<string | null>(null)
  const description = $derived(buildCodeGateDescription({ presentationMode, hasCodeInUrl }))

  const submit = () => {
    const normalized = normalizePortalCode(code)

    if (!normalized || normalized.length !== 8) {
      localError = "Bitte geben Sie den achtstelligen Code aus Ihrer Nachricht ein."

      return
    }

    localError = null
    onsubmit(normalized)
  }
</script>

<SectionTitle
  title="Bitte geben Sie Ihren Termincode ein."
  brightBackground={true}
  style="margin-bottom: 1.2rem;"
/>

<form
  onsubmit={(event) => {
    event.preventDefault()
    submit()
  }}
>
  <ActionBox label="Termincode" buttonSnippet={openAppointmentButton}>
    <p>{description}</p>
    <input
      id="appointment-code"
      bind:value={code}
      inputmode="text"
      autocomplete="one-time-code"
      maxlength="12"
      placeholder="ABC12345"
      aria-label="Termincode"
      aria-invalid={Boolean(localError || error)}
    />
  </ActionBox>
  {#if localError || error}
    <p class="error">{localError || error}</p>
  {/if}
</form>

{#snippet openAppointmentButton()}
  <CTA
    type="primaryButton"
    icon={mdiArrowRightCircleOutline}
    content={loading ? "Wird geprüft" : "Termin öffnen"}
    darkMode={false}
    disabled={loading}
    onClick={submit}
  />
{/snippet}

<style>
  form {
    --portal-ink: var(--bg-100);
    --portal-muted: #42514f;
    --portal-border: var(--bg-100);

    display: grid;
    gap: 0.8rem;
    color: var(--portal-ink);
    max-width: 680px;
  }

  p {
    margin: 0 0 0.8rem;
    color: var(--portal-muted);
    line-height: 1.4;
  }

  input {
    min-width: 12rem;
    width: min(100%, 18rem);
    border: 1px solid #8a9692;
    background: #fff;
    color: var(--portal-ink);
    padding: 0.85rem 1rem;
    font: inherit;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  input:focus {
    outline: none;
    border-color: var(--primary-200);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-200) 22%, transparent);
  }

  .error {
    margin: 0;
    color: #cf3f38;
    font-weight: 700;
  }
</style>
